<div class="fo-container">

  <div class="fo-header">
    <div>
      <h2 class="fo-title">Field order</h2>
      <p class="fo-subtitle">
        The order fields appear in on a Crop Nutrition Plan. It applies to this season only, and it
        spans farms, so farms may interleave if that is how you want to walk them.
      </p>
    </div>
    <div class="fo-header-actions">
      <button mat-button class="fo-text-btn" (click)="resetToDefault()" [disabled]="saving || isDemoData">
        Reset to default
      </button>
      <button mat-button class="fo-text-btn" (click)="undo()" [disabled]="!dirty || saving">
        Undo my changes
      </button>
      <button mat-flat-button class="fo-save" (click)="save()"
              [disabled]="fields.length === 0 || saving">
        {{ saving ? 'Saving...' : 'Save order' }}
      </button>
    </div>
  </div>

  <!-- Shown only when no season was loaded, so a demo is never mistaken for live data. -->
  <div class="fo-banner fo-banner--demo" *ngIf="isDemoData">
    <mat-icon class="fo-banner-icon">info</mat-icon>
    <span>Sample data. Nothing here is saved to a season.</span>
  </div>

  <!--
    A disabled drag with no explanation is worse than no drag at all, so the reason is on screen
    with the way out beside it.
  -->
  <div class="fo-banner fo-banner--sorted" *ngIf="!canDrag">
    <mat-icon class="fo-banner-icon">swap_vert</mat-icon>
    <span>
      Sorted by <strong>{{ sortColumn }}</strong> for viewing only. The saved order has not changed.
      Clear the sort to rearrange it.
    </span>
    <button mat-button (click)="clearSort()">Clear sort</button>
  </div>

  <p class="fo-hint" *ngIf="canDrag">
    Drag a row, or type a position. Select several and drag any one of them to move the whole set
    together.
    <span *ngIf="selected.size > 1" class="fo-hint-count">{{ selected.size }} selected.</span>
  </p>

  <div class="fo-loading" *ngIf="isLoading">
    <mat-spinner diameter="28"></mat-spinner>
    <span>Loading fields...</span>
  </div>

  <div class="fo-table" *ngIf="!isLoading">

    <div class="fo-head-row">
      <span class="fo-col-check">
        <mat-checkbox [checked]="allSelected" [indeterminate]="someSelected" (change)="toggleAll()"
                      aria-label="Select all fields"></mat-checkbox>
      </span>
      <span class="fo-col-handle"></span>
      <span class="fo-col-pos">#</span>
      <button class="fo-col-farm fo-sortable" (click)="sortBy('farmName')">
        Farm <mat-icon class="fo-sort-icon" *ngIf="sortColumn === 'farmName'">{{ sortAscending ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
      </button>
      <button class="fo-col-field fo-sortable" (click)="sortBy('fieldName')">
        Field <mat-icon class="fo-sort-icon" *ngIf="sortColumn === 'fieldName'">{{ sortAscending ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
      </button>
      <button class="fo-col-size fo-sortable" (click)="sortBy('size')">
        Size (ha) <mat-icon class="fo-sort-icon" *ngIf="sortColumn === 'size'">{{ sortAscending ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
      </button>
    </div>

    <!--
      cdkDropListLockAxis is not cosmetic: without it every pointer move computes a horizontal
      delta this list can never use, and the row drifts sideways under the cursor.

      No custom placeholder element. CDK's default is a clone of the row being dragged, so the gap
      is always exactly row sized. A hand rolled one of the wrong height makes the list shift and
      snap on every sort, which reads as lag. The line indicator is drawn ON that clone instead.
    -->
    <div class="fo-list" cdkDropList cdkDropListLockAxis="y"
         [cdkDropListDisabled]="!canDrag" (cdkDropListDropped)="onDrop($event)">

      <div class="fo-row" *ngFor="let field of view; trackBy: trackById"
           [class.fo-row--selected]="isSelected(field)"
           [class.fo-row--travelling]="isTravelling(field)"
           cdkDrag [cdkDragDisabled]="!canDrag"
           (cdkDragStarted)="onDragStarted(field)" (cdkDragEnded)="onDragEnded()">

        <!-- What the cursor carries. Says how many when several are moving together. -->
        <div class="fo-drag-preview" *cdkDragPreview>
          <mat-icon>drag_indicator</mat-icon>
          <span *ngIf="isSelected(field) && selected.size > 1">{{ selected.size }} fields</span>
          <span *ngIf="!(isSelected(field) && selected.size > 1)">{{ field.fieldName }}</span>
        </div>

        <span class="fo-col-check">
          <mat-checkbox [checked]="isSelected(field)" (change)="toggle(field)"
                        [attr.aria-label]="'Select ' + field.fieldName"></mat-checkbox>
        </span>

        <span class="fo-col-handle">
          <mat-icon class="fo-handle" cdkDragHandle aria-hidden="true"
                    [class.fo-handle--off]="!canDrag">drag_indicator</mat-icon>
        </span>

        <!--
          The position in the SAVED order, not the row's place in a sorted view, so the number keeps
          meaning the same thing whichever way the table is sorted.
        -->
        <span class="fo-col-pos">
          <input class="fo-pos-input" type="number" min="1" [max]="fields.length"
                 [value]="positionOf(field)" [disabled]="!canDrag"
                 (change)="moveTo(field, $any($event.target).value)"
                 [attr.aria-label]="'Position of ' + field.fieldName" />
        </span>

        <span class="fo-col-farm fo-text">{{ field.farmName }}</span>
        <span class="fo-col-field fo-text"><strong>{{ field.fieldName }}</strong></span>
        <span class="fo-col-size fo-text">{{ field.size != null ? (field.size | truncDecimals:2) : '-' }}</span>
      </div>

      <p class="fo-empty" *ngIf="fields.length === 0">This season has no fields to order yet.</p>
    </div>
  </div>

</div>
